// PRD.320 — composing a printable clinical summary from canonical encounter data.
//
// NO DUPLICATION. Every value here is READ from the domain that already owns it
// — Visit Note, Clinical Findings, Investigations, Diagnosis, Dental Treatment.
// Nothing is copied into prescription storage to make it printable, so a
// printout can never go stale relative to the record it describes.
//
// PRESENTATION LABELS vs DOMAINS. The paper prescription says "Treatment
// Advised" and "Treatment Done"; the repository has ONE dental treatment domain
// (`dental_treatments` + `dental_treatment_records`). These are two views of it,
// not two concepts — no parallel storage was introduced to match the wording.
//
// Self-contained (no workspace-package imports) so it runs under node:test.

// ─── Input row shapes ───
//
// Structural rather than importing each domain's row type: this module composes
// for print and must not become a hub that couples every clinical domain
// together.

export interface PrintFindingRow {
  text: string | null;
  teeth: string | null;
}

export interface PrintInvestigationRow {
  type: string | null;
  findings: string | null;
}

export interface PrintDiagnosisRow {
  id: string;
  label: string | null;
}

export interface PrintTreatmentRow {
  id: string;
  treatment: string | null;
  status: string | null;
}

export interface PrintTreatmentRecordRow {
  id: string;
  treatment: string | null;
  notes: string | null;
}

/** One printable clinical line: an optional context prefix plus its text. */
export interface PrintClinicalLine {
  /** Tooth/area context, e.g. `16 · 17` or '' when general. */
  context: string;
  /** The clinical text. Never empty — blank entries are dropped upstream. */
  text: string;
}

/**
 * Everything the Dental Structured template renders above Rx.
 *
 * Every field is a list so the template can collapse empty sections without
 * knowing anything about the underlying domains.
 */
export interface EncounterPrintClinical {
  chiefComplaint: string;
  clinicalFindings: PrintClinicalLine[];
  investigations: PrintClinicalLine[];
  diagnosis: PrintClinicalLine[];
  treatmentAdvised: PrintClinicalLine[];
  treatmentDone: PrintClinicalLine[];
}

/** The encounterless shape — every section empty, nothing undefined. */
export const EMPTY_ENCOUNTER_PRINT_CLINICAL: EncounterPrintClinical = {
  chiefComplaint: '',
  clinicalFindings: [],
  investigations: [],
  diagnosis: [],
  treatmentAdvised: [],
  treatmentDone: [],
};

// ─── Helpers ───

function clean(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function byFdi(a: string, b: string): number {
  return Number(a) - Number(b);
}

/** Compact tooth context: `16`, `16 · 17`, or '' when there is none. */
function formatTeeth(teeth: readonly string[]): string {
  const normalized = [...new Set(teeth.map((t) => String(t ?? '').trim()).filter(Boolean))].sort(
    byFdi,
  );
  return normalized.join(' · ');
}

/** Tooth context from a Clinical Findings `teeth` JSON blob. Never throws. */
function teethFromFindingJson(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return '';
    return formatTeeth(
      parsed
        .map((e) =>
          e && typeof e === 'object' ? String((e as { tooth?: unknown }).tooth ?? '') : '',
        )
        .filter(Boolean),
    );
  } catch {
    return '';
  }
}

// ─── Composition ───

/**
 * Build the printable clinical summary.
 *
 * Blank entries are dropped throughout: a finding with no text, an
 * investigation with neither type nor findings, a diagnosis with no label. An
 * enabled-but-empty section must print nothing at all rather than a bare
 * heading with a dash (PRD "Empty Section Behavior").
 *
 * `teethByX` maps carry already-grouped tooth context, so this module stays
 * free of each domain's association-table shape.
 */
export function composeEncounterPrintClinical(input: {
  chiefComplaint?: string | null;
  findings?: readonly PrintFindingRow[];
  investigations?: readonly PrintInvestigationRow[];
  investigationTeeth?: readonly string[][];
  diagnoses?: readonly PrintDiagnosisRow[];
  teethByDiagnosis?: ReadonlyMap<string, string[]>;
  treatments?: readonly PrintTreatmentRow[];
  teethByTreatment?: ReadonlyMap<string, string[]>;
  treatmentRecords?: readonly PrintTreatmentRecordRow[];
  teethByRecord?: ReadonlyMap<string, string[]>;
}): EncounterPrintClinical {
  const clinicalFindings: PrintClinicalLine[] = [];
  for (const f of input.findings ?? []) {
    const text = clean(f.text);
    if (!text) continue;
    clinicalFindings.push({ context: teethFromFindingJson(f.teeth), text });
  }

  const investigations: PrintClinicalLine[] = [];
  (input.investigations ?? []).forEach((iv, i) => {
    const type = clean(iv.type);
    const findings = clean(iv.findings);
    if (!type && !findings) return;
    // Type leads, findings follow — the printed line reads "IOPA — <finding>".
    const text = type && findings ? `${type} — ${findings}` : type || findings;
    investigations.push({ context: formatTeeth(input.investigationTeeth?.[i] ?? []), text });
  });

  const diagnosis: PrintClinicalLine[] = [];
  for (const d of input.diagnoses ?? []) {
    const text = clean(d.label);
    if (!text) continue;
    diagnosis.push({ context: formatTeeth(input.teethByDiagnosis?.get(d.id) ?? []), text });
  }

  // "Treatment Advised" = the ACTIVE plan. Done and cancelled items are not
  // advice: one is history (and appears under Treatment Done via its performed
  // records), the other was withdrawn.
  const treatmentAdvised: PrintClinicalLine[] = [];
  for (const t of input.treatments ?? []) {
    const status = clean(t.status).toLowerCase() || 'planned';
    if (status !== 'planned' && status !== 'in_progress') continue;
    const text = clean(t.treatment);
    if (!text) continue;
    treatmentAdvised.push({ context: formatTeeth(input.teethByTreatment?.get(t.id) ?? []), text });
  }

  // "Treatment Done" = performed RECORDS, not plans whose status is 'done'.
  // A status flag is workflow state; the record is what was actually performed.
  const treatmentDone: PrintClinicalLine[] = [];
  for (const r of input.treatmentRecords ?? []) {
    const treatment = clean(r.treatment);
    if (!treatment) continue;
    const notes = clean(r.notes);
    treatmentDone.push({
      context: formatTeeth(input.teethByRecord?.get(r.id) ?? []),
      text: notes ? `${treatment} — ${notes}` : treatment,
    });
  }

  return {
    chiefComplaint: clean(input.chiefComplaint),
    clinicalFindings,
    investigations,
    diagnosis,
    treatmentAdvised,
    treatmentDone,
  };
}

/**
 * Whether the Clinical Note block should render at all.
 *
 * The block's header appears only when at least one ENABLED subsection has
 * content, so a clinic that disabled everything — or an encounter with nothing
 * documented — prints no empty scaffolding.
 */
export function hasAnyPrintClinicalContent(
  clinical: EncounterPrintClinical,
  enabled: {
    complaint: boolean;
    clinicalFindings: boolean;
    investigations: boolean;
    diagnosis: boolean;
    treatmentAdvised: boolean;
    treatmentDone: boolean;
  },
): boolean {
  if (enabled.complaint && clinical.chiefComplaint) return true;
  if (enabled.clinicalFindings && clinical.clinicalFindings.length > 0) return true;
  if (enabled.investigations && clinical.investigations.length > 0) return true;
  if (enabled.diagnosis && clinical.diagnosis.length > 0) return true;
  if (enabled.treatmentAdvised && clinical.treatmentAdvised.length > 0) return true;
  if (enabled.treatmentDone && clinical.treatmentDone.length > 0) return true;
  return false;
}

// ─── PRD.320 — list rendering ───

/**
 * Render a clinical section's entries as configured.
 *
 * `lines` gives one entry per line; `comma` flows them together. Tooth context
 * stays attached to its own entry in both modes, so "16 · Caries" never becomes
 * "16, Caries".
 *
 * Empty entries are dropped BEFORE joining, which is what prevents the trailing
 * separator a naive `join(', ')` over a sparse list would leave behind.
 *
 * NOT for free text — Chief Complaint keeps its own line breaks (PRD "Free Text
 * Exception").
 */
export function formatClinicalLines(
  lines: readonly PrintClinicalLine[],
  layout: 'lines' | 'comma',
): string[] {
  const rendered = lines
    .map((l) => {
      const text = (l.text ?? '').trim();
      if (!text) return '';
      const context = (l.context ?? '').trim();
      return context ? `${context} · ${text}` : text;
    })
    .filter(Boolean);

  if (rendered.length === 0) return [];
  // A single flowing line, or one line each. `comma` still returns an array so
  // the caller renders both modes identically.
  return layout === 'comma' ? [rendered.join(', ')] : rendered;
}

// ─── PRD.320 — vitals presentation ───

/** The five vitals the print templates have a place for, in print order. */
export const PRINT_VITAL_FIELDS = [
  { key: 'height', label: 'Height' },
  { key: 'weight', label: 'Weight' },
  { key: 'bloodPressure', label: 'BP' },
  { key: 'pulse', label: 'Pulse' },
  { key: 'spo2', label: 'SpO2' },
] as const;

export type PrintVitalKey = (typeof PRINT_VITAL_FIELDS)[number]['key'];

export interface PrintVitalCell {
  key: PrintVitalKey;
  label: string;
  /** Display text. `—` in `table` mode when the value is missing. */
  value: string;
}

/**
 * Which vitals cells to draw, for a given layout.
 *
 * The three layouts deliberately disagree about a MISSING individual value:
 *
 *   table  — stable columns, so a gap is drawn as `—` and the other columns
 *            keep their positions
 *   inline — a flowing "Height: …, BP: …" sentence, so a gap is dropped; a
 *            placeholder there would read as a recorded value, and keeping the
 *            slot would leave a dangling separator
 *   strip  — compact label/value pairs, gaps dropped for the same reason
 *
 * They AGREE about an encounter with nothing recorded: an empty array, so the
 * caller omits the section entirely rather than printing a row of dashes.
 */
export function selectPrintVitals(
  vitals: Partial<Record<PrintVitalKey, string | null | undefined>>,
  layout: 'table' | 'inline' | 'strip',
): PrintVitalCell[] {
  const present = PRINT_VITAL_FIELDS.filter((f) => Boolean((vitals[f.key] ?? '').trim()));
  if (present.length === 0) return [];

  const source = layout === 'table' ? PRINT_VITAL_FIELDS : present;
  return source.map((f) => ({
    key: f.key,
    label: f.label,
    value: (vitals[f.key] ?? '').trim() || '—',
  }));
}
